import useJSnackbar from '@/hooks/useJSnackbar';
import Paths from '@/routes/paths';
import { JButton } from '@atoms/JButton';
import Jpaper from '@atoms/JPaper';
import { Loading } from '@atoms/Loading';
import { IconSize } from '@constants';
import useCreateEnterprise from '@enterprise/hooks/useCreateEnterprise';
import {
  AddressDto,
  EnterpriseDto,
} from '@enterprise/models/dto/enterprise-create.dto';
import {
  EnterpriseAddressFormDto,
  EnterpriseDetailsFormDto,
  EnterpriseVideoFormDto,
} from '@enterprise/models/enterprise-form';
import {
  useCreateEnterpriseRequest,
  useEditEnterpriseRequest,
} from '@enterprise/mutations/enterpriseMutations';
import { ArrowLeftIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { useCallback, useEffect } from 'react';

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

type valuesProp =
  | EnterpriseDetailsFormDto
  | EnterpriseAddressFormDto
  | EnterpriseVideoFormDto;

interface Props {
  isValid: boolean;
  values: valuesProp;
  submitTab?: boolean;
}

const EnterpriseFormFooter = ({ isValid, values, submitTab }: Props) => {
  const { t } = useTranslation();
  const {
    mutate: createEnterpriseMutation,
    isLoading: isCreateLoading,
    isSuccess: isCreateSuccess,
    isError: isCreateError,
  } = useCreateEnterpriseRequest();
  const {
    mutate: editEnterpriseMutation,
    isLoading: isEditLoading,
    isError: isEditError,
  } = useEditEnterpriseRequest();

  const navigation = useNavigate();
  const { showSnackbar } = useJSnackbar();
  const { setStepIndex, stepIndex, payload, mode } = useCreateEnterprise();

  const onNextClick = async (formValues: valuesProp) => {
    if (!submitTab) setStepIndex((prev) => (prev ? prev + 1 : 1));
    else if (submitTab) {
      handleSubmit();
    }
  };
  const onPreviousClick = async (formValues: valuesProp) => {
    setStepIndex((prev) => (prev ? prev + -1 : 1));
  };

  const appendField = (
    formData: FormData,
    enterpriseDto: EnterpriseDto,
    key: string,
  ) => {
    if (Object.hasOwn(enterpriseDto, key)) {
      const value = getEnterpriseDtoValue(enterpriseDto, key);
      if (Array.isArray(value)) {
        if (value.length === 1) {
          formData.append(`enterpriseDto[${key}][]`, value[0]);
        } else {
          value.forEach((item) =>
            formData.append(`enterpriseDto[${key}]`, item),
          );
        }
      } else if (value !== undefined) {
        formData.append(`enterpriseDto[${key}]`, value);
      }
    }
  };

  const appendDatatoForm = (formData: FormData) => {
    // Append the fields from enterpriseDto
    if (payload?.enterpriseDto) {
      const enterpriseDto = payload.enterpriseDto;
      for (const key in enterpriseDto) {
        appendField(formData, enterpriseDto, key);
      }
    }

    // Append the fields from addressDto
    if (payload?.addressDto) {
      const addressDto = payload.addressDto;
      for (const key in addressDto) {
        if (Object.hasOwn(addressDto, key)) {
          const value = getAddressDtoValue(addressDto, key);

          if (value !== undefined) formData.append(`addressDto[${key}]`, value);
        }
      }
    }

    if (payload?.video) formData.append('video', payload?.video);
    if (payload?.logo) formData.append('logo', payload?.logo);
    if (payload?.thumbnail) formData.append('thumbnail', payload?.thumbnail);
  };
  const handleSubmit = async () => {
    const formData = new FormData();
    appendDatatoForm(formData);
    try {
      // Perform  API request using the FormData object
      mode === 'create'
        ? createEnterpriseMutation({ formData })
        : payload?.id && editEnterpriseMutation({ formData, id: payload?.id });
    } catch (error) {
      showSnackbar(t('API_ERROR'), 'error');
      console.error(`error while ${error} enterprise`, error);
    }
  };

  const getEnterpriseDtoValue = (
    data: EnterpriseDto,
    key: string,
  ): string | string[] | undefined | Blob => {
    const value = data[key as keyof EnterpriseDto];

    if (value !== undefined) {
      if (typeof value === 'string') {
        // Remove quotes and trim if the value is a string
        return value.replace(/"/g, '').trim();
      } else {
        return value;
      }
    }
  };

  const getAddressDtoValue = (
    data: AddressDto,
    key: string,
  ): string | undefined | Blob => {
    const value = data[key as keyof AddressDto];

    if (value !== undefined) {
      if (typeof value === 'string') {
        // Remove quotes and trim if the value is a string
        return value.replace(/"/g, '').trim();
      } else {
        return value;
      }
    }
  };

  const getLabel = useCallback(() => {
    if (submitTab && mode === 'create') {
      return t('CREATE_COMPANY');
    } else if (submitTab && mode === 'edit') {
      return t('EDIT_COMPANY');
    } else {
      return t('NEXT');
    }
  }, [submitTab, mode, t]);

  useEffect(() => {
    if (isCreateSuccess) {
      showSnackbar(t('ENTERPRISE_CREATE_SUCCESS'), 'success');
      navigation(Paths.EnterprisePage);
    }
  }, [isCreateSuccess]);

  useEffect(() => {
    if (isCreateError) {
      showSnackbar(t('API_ERROR'), 'error');
    }
  }, [isCreateError]);

  useEffect(() => {
    if (isEditError) {
      showSnackbar(t('API_ERROR'), 'error');
    }
  }, [isEditError]);

  if (isCreateLoading || isEditLoading) return <Loading />;
  return (
    <Jpaper direction="row" justifyContent="space-between" alignItems="center">
      {stepIndex ? (
        <JButton
          data-qa="previous-button"
          disabled={isCreateLoading || isEditLoading}
          onClick={() => onPreviousClick(values)}
          startIcon={<ArrowLeftIcon height={IconSize.md} />}
          variant="text"
        >
          {t('PREVIOUS')}
        </JButton>
      ) : (
        <span></span>
      )}
      <JButton
        data-qa="submit-button"
        onClick={() => onNextClick(values)}
        endIcon={<ArrowRightIcon height={IconSize.md} />}
        variant="contained"
        disabled={!isValid || isCreateLoading || isEditLoading}
      >
        {getLabel()}
      </JButton>
    </Jpaper>
  );
};

export default EnterpriseFormFooter;
